import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Agreement, AgreementHeading, AgreementParagraph } from '@/components/ui/agreement';
import ExternalLink from '@/components/ui/external-link';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { useProfilePrivacyMutation } from '@/hooks/queries/use-profile-privacy';

const VerificationPrivacyNotice = ({ onAccepted }: { onAccepted: VoidFunction }) => {
  const { isPending, isSuccess, mutate } = useProfilePrivacyMutation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isRead, setIsRead] = useState(false);

  const handleAccept = () => {
    mutate(undefined, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => onAccepted(),
    });
  };

  return (
    <PageCard className="-md:flex-1 -md:flex -md:overflow-hidden" size="lg" bordered fixedFooter>
      <PageCardContent className="-md:flex-1 -md:overflow-hidden pt-8">
        <Agreement className="-md:flex-1" onScrollEnd={() => setIsRead(true)}>
          <AgreementParagraph>
            <Trans
              i18nKey="privacyNotice.text"
              values={{
                sumsubUrl: t('privacyNotice.sumsubUrl'),
                oneTradingUrl: t('privacyNotice.dataPrivacyPolicy'),
              }}
              components={[
                <ExternalLink key="privacyNotice.text.sumsubUrl" href={t('privacyNotice.sumsubUrl')} className="underline hover:no-underline">
                  {t('privacyNotice.sumsubUrl')}
                </ExternalLink>,
                <ExternalLink key="privacyNotice.text.oneTradingUrl" href={t('privacyNotice.oneTradingUrl')} className="underline hover:no-underline">
                  {t('privacyNotice.dataPrivacyPolicy')}
                </ExternalLink>,
              ]}
            />
          </AgreementParagraph>

          <AgreementHeading>{t('privacyNotice.categoriesOfBiometricDataTitle')}</AgreementHeading>
          <AgreementParagraph>{t('privacyNotice.categoriesOfBiometricDataBody')}</AgreementParagraph>
          <AgreementParagraph>{t('privacyNotice.acknowledgment')}</AgreementParagraph>

          <AgreementHeading>{t('privacyNotice.purposesOfProcessingTitle')}</AgreementHeading>
          <AgreementParagraph>{t('privacyNotice.purposesOfProcessingBody')}</AgreementParagraph>
          <AgreementParagraph>
            <Trans
              i18nKey="privacyNotice.processingBiometricBody"
              values={{
                sumsubUrl: t('privacyNotice.sumsubUrl'),
              }}
              components={[
                <ExternalLink key="privacyNotice.text.sumsubUrl" href={t('privacyNotice.sumsubUrl')} className="underline hover:no-underline">
                  {t('privacyNotice.sumsubUrl')}
                </ExternalLink>,
              ]}
            />
          </AgreementParagraph>

          <AgreementHeading>{t('privacyNotice.howDataProcessedTitle')}</AgreementHeading>
          <AgreementParagraph>
            <Trans
              i18nKey="privacyNotice.howDataProcessedBody"
              values={{
                sumsubUrl: t('privacyNotice.sumsubUrl'),
              }}
              components={[
                <ExternalLink key="privacyNotice.text.sumsubUrl" href={t('privacyNotice.sumsubUrl')} className="underline hover:no-underline">
                  {t('privacyNotice.sumsubUrl')}
                </ExternalLink>,
              ]}
            />
          </AgreementParagraph>

          <AgreementParagraph>{t('privacyNotice.biometricDisclosedBody')}</AgreementParagraph>

          <AgreementHeading>{t('privacyNotice.retentionOfDataTitle')}</AgreementHeading>
          <AgreementParagraph>
            <Trans
              i18nKey="privacyNotice.retentionOfDataBody"
              values={{
                sumsubUrl: t('privacyNotice.sumsubUrl'),
              }}
              components={[
                <ExternalLink key="privacyNotice.text.sumsubUrl" href={t('privacyNotice.sumsubUrl')} className="underline hover:no-underline">
                  {t('privacyNotice.sumsubUrl')}
                </ExternalLink>,
              ]}
            />
          </AgreementParagraph>

          <AgreementParagraph>{t('privacyNotice.revokeConsent')}</AgreementParagraph>
          <AgreementParagraph>{t('privacyNotice.agreement')}</AgreementParagraph>
        </Agreement>
      </PageCardContent>
      <PageCardFooter>
        <Button className="w-full relative" size="lg" type="submit" variant="primary" disabled={!isRead || isPending || isSuccess} onClick={handleAccept}>
          <span className={isPending ? 'invisible' : ''}>{t('buttons.agreeAndContinue')}</span>
          {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
        </Button>
      </PageCardFooter>
    </PageCard>
  );
};
VerificationPrivacyNotice.displayName = 'VerificationPrivacyNotice';

export default VerificationPrivacyNotice;
